import React, { useRef, useEffect } from 'react'
import { useThree } from '@react-three/fiber'
import { useScene } from '../context/SceneContext'
import * as THREE from 'three'

const SurfaceSnapHandler = () => {
  const { camera, scene, raycaster, pointer } = useThree()
  const { selectedObject, transformMode, updateObject, objects } = useScene()
  const snapIndicatorRef = useRef()
  const snapPointRef = useRef(null)
  const snapNormalRef = useRef(null)

  // Create snap indicator (small sphere that shows where object will be placed)
  useEffect(() => {
    if (snapIndicatorRef.current) {
      snapIndicatorRef.current.visible = false
    }
  }, [])

  const handlePointerMove = (event) => {
    if (transformMode !== 'move' || !selectedObject) {
      if (snapIndicatorRef.current) {
        snapIndicatorRef.current.visible = false
      }
      return
    }

    // Update raycaster with current mouse position
    raycaster.setFromCamera(pointer, camera)

    // Get all meshes in the scene except the selected object and snap indicator
    const intersectableObjects = []
    scene.traverse((child) => {
      if (child.isMesh && 
          child.name !== `object-${selectedObject.id}` && 
          child.name !== 'snap-indicator' &&
          child.name !== 'ground-plane' &&
          !child.name.includes('wireframe')) {
        intersectableObjects.push(child)
      }
    })

    // Add ground plane for fallback
    const groundPlane = new THREE.Mesh(
      new THREE.PlaneGeometry(50, 50),
      new THREE.MeshBasicMaterial({ visible: false })
    )
    groundPlane.rotation.x = -Math.PI / 2
    groundPlane.position.set(0, 0, 0)
    groundPlane.name = 'ground-plane'
    intersectableObjects.push(groundPlane)

    // Perform raycasting
    const intersects = raycaster.intersectObjects(intersectableObjects, false)

    if (intersects.length > 0) {
      const intersection = intersects[0]
      const point = intersection.point
      const normal = intersection.normal
      const intersectedObject = intersection.object

      // Calculate snap position based on the selected object's bounding box
      const snapPosition = calculateSnapPosition(point, normal, selectedObject)
      
      // Store snap data
      snapPointRef.current = snapPosition
      snapNormalRef.current = normal

      // Update snap indicator
      if (snapIndicatorRef.current) {
        snapIndicatorRef.current.position.copy(snapPosition)
        snapIndicatorRef.current.visible = true
        
        // Change color based on surface type
        const material = snapIndicatorRef.current.material
        if (intersectedObject.name === 'ground-plane') {
          material.color.setHex(0x00ff00) // Green for ground
          material.emissive.setHex(0x002200) // Slight green glow
        } else {
          material.color.setHex(0xff6600) // Orange for object surfaces
          material.emissive.setHex(0x221100) // Slight orange glow
        }
      }
    } else {
      // Hide snap indicator if no intersection
      if (snapIndicatorRef.current) {
        snapIndicatorRef.current.visible = false
      }
      snapPointRef.current = null
      snapNormalRef.current = null
    }
  }

  const calculateSnapPosition = (intersectionPoint, surfaceNormal, objectToPlace) => {
    // Get the bounding box of the object to place
    const boundingBox = getObjectBoundingBox(objectToPlace)
    const objectHeight = boundingBox.max.y - boundingBox.min.y
    const objectWidth = Math.max(
      boundingBox.max.x - boundingBox.min.x,
      boundingBox.max.z - boundingBox.min.z
    )

    // Calculate offset based on surface normal and object dimensions
    let offset = new THREE.Vector3()
    
    // For vertical surfaces (walls), offset by half the object width
    if (Math.abs(surfaceNormal.y) < 0.5) {
      offset = surfaceNormal.clone().multiplyScalar(objectWidth * 0.5 + 0.01) // Small gap
    } 
    // For horizontal surfaces (floors/ceilings), offset by half the object height
    else {
      // For upward-facing surfaces (floors)
      if (surfaceNormal.y > 0) {
        offset = surfaceNormal.clone().multiplyScalar(objectHeight * 0.5 + 0.01)
      } 
      // For downward-facing surfaces (ceilings)
      else {
        offset = surfaceNormal.clone().multiplyScalar(-(objectHeight * 0.5 + 0.01))
      }
    }

    return intersectionPoint.clone().add(offset)
  }

  const getObjectBoundingBox = (object) => {
    // Return approximate bounding boxes for different object types
    const boundingBoxes = {
      cube: { min: { x: -0.5, y: -0.5, z: -0.5 }, max: { x: 0.5, y: 0.5, z: 0.5 } },
      sphere: { min: { x: -0.5, y: -0.5, z: -0.5 }, max: { x: 0.5, y: 0.5, z: 0.5 } },
      cylinder: { min: { x: -0.5, y: -0.5, z: -0.5 }, max: { x: 0.5, y: 0.5, z: 0.5 } },
      cone: { min: { x: -0.5, y: -0.5, z: -0.5 }, max: { x: 0.5, y: 0.5, z: 0.5 } },
      torus: { min: { x: -0.7, y: -0.2, z: -0.7 }, max: { x: 0.7, y: 0.2, z: 0.7 } },
      plane: { min: { x: -0.5, y: -0.01, z: -0.5 }, max: { x: 0.5, y: 0.01, z: 0.5 } },
      'grip-handle': { min: { x: -0.3, y: -1, z: -0.3 }, max: { x: 0.3, y: 1, z: 0.3 } },
      'button-large': { min: { x: -0.4, y: -0.1, z: -0.4 }, max: { x: 0.4, y: 0.1, z: 0.4 } },
      ramp: { min: { x: -1, y: -0.05, z: -0.5 }, max: { x: 1, y: 0.05, z: 0.5 } },
      lever: { min: { x: -0.75, y: -0.05, z: -0.05 }, max: { x: 0.75, y: 0.05, z: 0.05 } },
      'braille-dot': { min: { x: -0.05, y: -0.05, z: -0.05 }, max: { x: 0.05, y: 0.05, z: 0.05 } }
    }

    const bbox = boundingBoxes[object.type] || boundingBoxes.cube
    
    // Apply object scale
    return {
      min: new THREE.Vector3(
        bbox.min.x * object.scale[0],
        bbox.min.y * object.scale[1],
        bbox.min.z * object.scale[2]
      ),
      max: new THREE.Vector3(
        bbox.max.x * object.scale[0],
        bbox.max.y * object.scale[1],
        bbox.max.z * object.scale[2]
      )
    }
  }

  const handleClick = (event) => {
    if (transformMode === 'move' && selectedObject && snapPointRef.current) {
      event.stopPropagation()
      
      // Move object to snap position
      updateObject(selectedObject.id, {
        position: [
          snapPointRef.current.x,
          snapPointRef.current.y,
          snapPointRef.current.z
        ]
      })

      console.log('🎯 Surface snap: Object moved to', snapPointRef.current)
      console.log('🎯 Surface normal:', snapNormalRef.current)
    }
  }

  // Add event listeners
  useEffect(() => {
    const canvas = document.querySelector('canvas')
    if (canvas) {
      canvas.addEventListener('pointermove', handlePointerMove)
      canvas.addEventListener('click', handleClick)
      
      return () => {
        canvas.removeEventListener('pointermove', handlePointerMove)
        canvas.removeEventListener('click', handleClick)
      }
    }
  }, [transformMode, selectedObject, objects])

  // Only render when in move mode with selected object
  if (transformMode !== 'move' || !selectedObject) {
    return null
  }

  return (
    <>
      {/* Snap indicator - shows where object will be placed */}
      <mesh ref={snapIndicatorRef} name="snap-indicator">
        <sphereGeometry args={[0.08, 12, 12]} />
        <meshBasicMaterial 
          color="#00ff00" 
          transparent 
          opacity={0.9}
          depthTest={false}
        />
      </mesh>

      {/* Invisible ground plane for fallback snapping */}
      <mesh
        position={[0, 0, 0]}
        rotation={[-Math.PI / 2, 0, 0]}
        name="ground-plane"
      >
        <planeGeometry args={[50, 50]} />
        <meshBasicMaterial 
          transparent 
          opacity={0}
          side={THREE.DoubleSide}
        />
      </mesh>
    </>
  )
}

export default SurfaceSnapHandler
